/**
 * CONSAI — Plan Comparison Page (full dark, glow cards aesthetic)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { GlowCard } from '../components/ui/glow-card';
import {
  Crown, Check, X, ArrowLeft, Sparkles, Users, FileText, MessageSquare,
  BarChart3, Mic, Heart, LineChart, Download, Bell, Phone, Image, Package
} from 'lucide-react';
import { getFeatureColor } from '../lib/feature-colors';

const PlanComparison = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const currentPlan = user?.current_plan || 'starter';
  const isPremium = currentPlan === 'premium';
  const isTrial = currentPlan === 'premium_trial';

  const blue = getFeatureColor('analytics');
  const ai = getFeatureColor('ai');

  const features = [
    {
      category: language === 'hi' ? 'मूलभूत' : 'Basics',
      items: [
        { name: language === 'hi' ? 'ग्राहक' : 'Customers', starter: language === 'hi' ? '50 तक' : 'Up to 50', premium: language === 'hi' ? 'असीमित' : 'Unlimited', icon: Users },
        { name: language === 'hi' ? 'मासिक इनवॉइस' : 'Invoices per month', starter: '50', premium: language === 'hi' ? 'असीमित' : 'Unlimited', icon: FileText },
        { name: language === 'hi' ? 'टीम सदस्य' : 'Team Members', starter: language === 'hi' ? '2 कुल' : '2 total', premium: language === 'hi' ? '5 कुल' : '5 total', icon: Users },
      ]
    },
    {
      category: language === 'hi' ? 'स्टॉक प्रबंधन' : 'Stock Management',
      items: [
        { name: language === 'hi' ? 'स्टॉक ट्रैकिंग' : 'Stock Tracking', starter: true, premium: true, icon: Package },
        { name: language === 'hi' ? 'आइटम इमेज' : 'Item Images', starter: true, premium: true, icon: Image },
        { name: language === 'hi' ? 'कम स्टॉक अलर्ट' : 'Low Stock Alerts', starter: language === 'hi' ? 'बेसिक' : 'Basic', premium: language === 'hi' ? 'एडवांस्ड' : 'Advanced', icon: Bell },
      ]
    },
    {
      category: language === 'hi' ? 'AI फीचर्स' : 'AI Features',
      items: [
        { name: language === 'hi' ? 'AI प्रश्न' : 'AI Questions', starter: language === 'hi' ? 'सप्ताह में 10' : '10 per week', premium: language === 'hi' ? 'दिन में 50' : '50 per day', icon: MessageSquare },
        { name: language === 'hi' ? 'AI सारांश' : 'AI Summary', starter: language === 'hi' ? 'साप्ताहिक' : 'Weekly', premium: language === 'hi' ? 'दैनिक (ऑप्ट-इन)' : 'Daily (opt-in)', icon: Sparkles },
        { name: language === 'hi' ? 'AI सुझाव पैनल' : 'AI Suggestions Panel', starter: false, premium: true, icon: Sparkles },
        { name: language === 'hi' ? 'वॉयस इनपुट' : 'Voice Input', starter: language === 'hi' ? 'केवल सर्च' : 'Search only', premium: language === 'hi' ? 'AI कमांड' : 'AI Commands', icon: Mic },
      ]
    },
    {
      category: language === 'hi' ? 'एनालिटिक्स' : 'Analytics',
      items: [
        { name: language === 'hi' ? 'एडवांस्ड एनालिटिक्स' : 'Advanced Analytics', starter: false, premium: true, icon: BarChart3 },
        { name: language === 'hi' ? 'सेल्स हीटमैप' : 'Sales Heatmap', starter: false, premium: true, icon: BarChart3 },
        { name: language === 'hi' ? 'क्रेडिट हेल्थ स्कोर' : 'Credit Health Score', starter: false, premium: true, icon: Heart },
        { name: language === 'hi' ? 'रेवेन्यू फोरकास्टिंग' : 'Revenue Forecasting', starter: false, premium: true, icon: LineChart },
      ]
    },
    {
      category: language === 'hi' ? 'टूल्स और एक्सपोर्ट' : 'Tools & Export',
      items: [
        { name: language === 'hi' ? 'CSV एक्सपोर्ट' : 'CSV Export', starter: false, premium: true, icon: Download },
        { name: language === 'hi' ? 'भुगतान रिमाइंडर' : 'Payment Reminders', starter: false, premium: true, icon: Bell },
        { name: 'WhatsApp Summary', starter: true, premium: true, icon: Phone },
      ]
    },
  ];

  const renderValue = (val, isPremiumCol) => {
    if (val === true) return <Check className="h-5 w-5 mx-auto" style={{ color: isPremiumCol ? ai.text : blue.text }} />;
    if (val === false) return <X className="h-5 w-5 text-white/20 mx-auto" />;
    return <span className="text-sm font-medium text-white/80">{val}</span>;
  };

  const starterFeatures = [
    language === 'hi' ? '50 ग्राहकों तक' : 'Up to 50 customers',
    language === 'hi' ? 'मासिक 50 इनवॉइस' : '50 invoices/month',
    language === 'hi' ? 'बेसिक स्टॉक ट्रैकिंग' : 'Basic stock tracking',
    language === 'hi' ? 'सप्ताह में 10 AI प्रश्न' : '10 AI questions/week',
    language === 'hi' ? 'साप्ताहिक AI सारांश' : 'Weekly AI summary',
  ];

  const premiumFeatures = [
    language === 'hi' ? 'असीमित ग्राहक' : 'Unlimited customers',
    language === 'hi' ? 'असीमित इनवॉइस' : 'Unlimited invoices',
    language === 'hi' ? 'एडवांस्ड एनालिटिक्स' : 'Advanced analytics',
    language === 'hi' ? 'AI सुझाव पैनल' : 'AI suggestions panel',
    language === 'hi' ? 'दिन में 50 AI प्रश्न' : '50 AI questions/day',
    language === 'hi' ? 'AI वॉयस कमांड' : 'AI voice commands',
    language === 'hi' ? 'CSV एक्सपोर्ट' : 'CSV export',
    language === 'hi' ? 'भुगतान रिमाइंडर' : 'Payment reminders',
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative">
      <div className="pointer-events-none fixed inset-0 app-bg-spotlight" />

      <header className="sticky top-0 z-10 border-b border-white/10"
        style={{ background: 'rgba(10,10,11,0.78)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white hover:bg-white/5" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold tracking-tight text-white" data-testid="plan-comparison-title">
            {language === 'hi' ? 'प्लान तुलना' : 'Plan Comparison'}
          </h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-8 space-y-8 relative">

        {/* Trial banner */}
        {isTrial && (
          <GlowCard feature="ai" size="auto" innerClassName="p-6 md:p-8 text-center" data-testid="trial-banner">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <Crown className="h-10 w-10 mx-auto mb-3 text-yellow-300" />
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-white">
              {language === 'hi' ? 'आपका 14-दिन प्रीमियम ट्रायल चल रहा है!' : 'Your 14-Day Premium Trial is Active!'}
            </h2>
            <p className="text-white/65 mt-2">
              {language === 'hi' ? 'सभी प्रीमियम फीचर्स का अनुभव लें' : 'Experience all premium features'}
            </p>
          </GlowCard>
        )}

        {/* Two plan cards */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {/* Starter */}
          <GlowCard feature="analytics" size="auto" innerClassName="p-6 md:p-8" data-testid="starter-plan-card">
            {currentPlan === 'starter' && (
              <div className="absolute top-4 right-4">
                <Badge style={{ background: blue.hex, color: '#FFFFFF', border: 'none' }}>
                  {language === 'hi' ? 'वर्तमान' : 'Current Plan'}
                </Badge>
              </div>
            )}
            <p className="text-base font-semibold tracking-tight" style={{ color: blue.text }}>
              {language === 'hi' ? 'स्टार्टर' : 'Starter'}
            </p>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-4xl md:text-5xl font-semibold text-white tracking-tight">₹19</span>
              <span className="text-white/45 text-sm">/{language === 'hi' ? 'माह' : 'month'}</span>
            </div>
            <p className="text-sm text-white/55 mt-1">
              {language === 'hi' ? 'छोटे व्यवसायों के लिए' : 'For small businesses'}
            </p>
            <ul className="mt-5 space-y-2.5">
              {starterFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/75">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: blue.text }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => currentPlan !== 'starter' && navigate('/payment?type=starter')}
              disabled={currentPlan === 'starter'}
              className="w-full mt-6 h-12 transition-all active:scale-[0.98] rounded-xl"
              style={{
                background: currentPlan === 'starter' ? 'rgba(255,255,255,0.06)' : blue.hex,
                color: currentPlan === 'starter' ? 'rgba(255,255,255,0.45)' : '#FFFFFF',
              }}
              data-testid="select-starter-btn"
            >
              {currentPlan === 'starter'
                ? (language === 'hi' ? 'वर्तमान प्लान' : 'Current Plan')
                : (language === 'hi' ? 'स्टार्टर चुनें' : 'Choose Starter')}
            </Button>
          </GlowCard>

          {/* Premium */}
          <GlowCard feature="ai" size="auto" innerClassName="p-6 md:p-8" data-testid="premium-plan-card">
            <div className="absolute -top-2 left-1/2 -translate-x-1/2">
              <Badge className="bg-amber-500 text-white border-0 text-[10px] px-2.5 py-1">
                {language === 'hi' ? 'लोकप्रिय' : 'POPULAR'}
              </Badge>
            </div>
            {(isPremium || isTrial) && (
              <div className="absolute top-4 right-4">
                <Badge style={{ background: ai.hex, color: '#FFFFFF', border: 'none' }}>
                  {isTrial
                    ? (language === 'hi' ? 'ट्रायल पर' : 'On Trial')
                    : (language === 'hi' ? 'वर्तमान' : 'Current Plan')}
                </Badge>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Crown className="h-5 w-5" style={{ color: ai.text }} />
              <p className="text-base font-semibold tracking-tight" style={{ color: ai.text }}>
                {language === 'hi' ? 'प्रीमियम' : 'Premium'}
              </p>
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-4xl md:text-5xl font-semibold text-white tracking-tight">₹99</span>
              <span className="text-white/45 text-sm">/{language === 'hi' ? 'माह' : 'month'}</span>
            </div>
            <p className="text-sm text-white/55 mt-1">
              {language === 'hi' ? 'पूरी क्षमता के लिए' : 'For unlimited growth'}
            </p>
            <ul className="mt-5 space-y-2.5">
              {premiumFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/75">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: ai.text }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => !isPremium && navigate('/payment?type=premium')}
              disabled={isPremium}
              className="w-full mt-6 h-12 transition-all active:scale-[0.98] rounded-xl font-medium"
              style={{
                background: isPremium ? 'rgba(255,255,255,0.06)' : ai.hex,
                color: isPremium ? 'rgba(255,255,255,0.45)' : '#FFFFFF',
              }}
              data-testid="select-premium-btn"
            >
              {isPremium
                ? (language === 'hi' ? 'वर्तमान प्लान' : 'Current Plan')
                : (language === 'hi' ? 'प्रीमियम पर अपग्रेड करें' : 'Upgrade to Premium')}
            </Button>
          </GlowCard>
        </div>

        {/* Feature comparison */}
        <section>
          <h2 className="text-xl md:text-2xl font-semibold text-white tracking-tight mb-5">
            {language === 'hi' ? 'फीचर तुलना' : 'Feature Comparison'}
          </h2>
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0E0E10]">
            {/* Table header */}
            <div className="grid grid-cols-3 px-4 md:px-6 py-3 text-xs font-semibold uppercase tracking-wider border-b border-white/10 bg-white/[0.02]">
              <span className="text-white/45">{language === 'hi' ? 'फीचर' : 'Feature'}</span>
              <span className="text-center" style={{ color: blue.text }}>{language === 'hi' ? 'स्टार्टर' : 'Starter'}</span>
              <span className="text-center" style={{ color: ai.text }}>{language === 'hi' ? 'प्रीमियम' : 'Premium'}</span>
            </div>
            {features.map((cat, catIdx) => (
              <div key={catIdx}>
                <div className="px-4 md:px-6 py-2 bg-white/[0.03] border-b border-white/5 text-xs font-semibold uppercase tracking-wider text-white/40">
                  {cat.category}
                </div>
                {cat.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-3 items-center px-4 md:px-6 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center gap-2 text-sm text-white/75">
                      <item.icon className="h-4 w-4 text-white/40 flex-shrink-0" />
                      <span>{item.name}</span>
                    </div>
                    <div className="text-center">{renderValue(item.starter, false)}</div>
                    <div className="text-center">{renderValue(item.premium, true)}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        {!isPremium && (
          <GlowCard feature="ai" size="auto" innerClassName="p-6 md:p-8 text-center" data-testid="bottom-cta">
            <Sparkles className="h-9 w-9 mx-auto mb-3" style={{ color: ai.text }} />
            <h3 className="text-xl md:text-2xl font-semibold text-white tracking-tight mb-2">
              {language === 'hi' ? 'अपने व्यवसाय को बदलने के लिए तैयार हैं?' : 'Ready to transform your business?'}
            </h3>
            <p className="text-white/65 mb-5 max-w-md mx-auto">
              {language === 'hi' ? 'आज ही प्रीमियम पर अपग्रेड करें और सभी सुविधाओं को अनलॉक करें' : 'Upgrade to Premium today and unlock all features'}
            </p>
            <Button
              onClick={() => navigate('/payment?type=premium')}
              className="h-12 px-8 transition-all active:scale-[0.98] rounded-xl font-medium"
              style={{ background: ai.hex, color: '#FFFFFF' }}
              data-testid="upgrade-now-btn"
            >
              <Crown className="h-4 w-4 mr-2" />
              {language === 'hi' ? 'अभी अपग्रेड करें' : 'Upgrade Now'}
            </Button>
          </GlowCard>
        )}
      </main>
    </div>
  );
};

export default PlanComparison;
